import { asyncReducer, useAsync } from 'hooks';
import { useAfterFirstRenderEffect } from 'hooks/utils/useAfterFirstRenderEffect/useAfterFirstRenderEffect';
import {
  getAsyncAddItemPayload,
  getAsyncAddItemsPayload,
  getAsyncApplyPredicatePayload,
  getAsyncApplyTransformPayload,
} from 'hooks/utils/useAsync/utils';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { IChildrenProps } from 'types/ChildrenProps';
import useSchedulingTimeOffQuery from '../hooks/useSchedulingTimeOffQuery';
import { SchedulingTimeOff } from '../types/types';
import { transformTimeOffToScheduleEvent } from '../utils/utils';

export type ISchedulingTimeOffDataContextState = ReturnType<typeof useSchedulingTimeOffData>;

export const SchedulingTimeOffDataContext = React.createContext<ISchedulingTimeOffDataContextState | null>(null);

export function useSchedulingTimeOffDataState() {
  const context = useContext(SchedulingTimeOffDataContext);

  if (context === null) {
    throw Error('`useSchedulingTimeOffDataState` must be nested within a `SchedulingTimeOffDataContextProvider.`');
  }

  return context!;
}

type ISchedulingTimeOffDataContextProviderProps = IChildrenProps;

type SchedulingTimeOffWithoutCursor = Omit<SchedulingTimeOff, 'cursor'>;

export function SchedulingTimeOffDataContextProvider({ children }: ISchedulingTimeOffDataContextProviderProps) {
  return (
    <SchedulingTimeOffDataContext.Provider value={useSchedulingTimeOffData()}>
      {children}
    </SchedulingTimeOffDataContext.Provider>
  );
}

function useSchedulingTimeOffData() {
  const query = useSchedulingTimeOffQuery();

  const { data, ...rest } = useAsync(
    useCallback(async () => {
      const timeOffs = await query();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      return timeOffs.map(({ cursor, ...rest }) => ({ ...rest }));
    }, [query]),
    true,
    asyncReducer,
    false
  );

  useAfterFirstRenderEffect(() => {
    rest.execute();
  }, [query]);

  const memoizedData = useMemo(() => data?.map(transformTimeOffToScheduleEvent) ?? [], [data]);

  return useMemo(
    () => ({
      events: memoizedData,
      addTimeOff: function (timeOff: SchedulingTimeOffWithoutCursor) {
        rest.dispatch(getAsyncAddItemPayload(data ?? [], timeOff));
      },
      addTimeOffs: function (timeOffs: Array<SchedulingTimeOffWithoutCursor>) {
        rest.dispatch(getAsyncAddItemsPayload(data ?? [], timeOffs));
      },
      removeTimeOff: function (id: string) {
        rest.dispatch(getAsyncApplyPredicatePayload(data ?? [], (value) => value.id !== id));
      },
      removeTimeOffs: function (ids: string[]) {
        const idSet = new Set(ids);
        rest.dispatch(getAsyncApplyPredicatePayload(data ?? [], (value) => !idSet.has(value.id)));
      },
      updateTimeOffEventKey: function <K extends keyof SchedulingTimeOff>(
        id: string,
        key: K,
        updatedValue: SchedulingTimeOff[K]
      ) {
        rest.dispatch(
          getAsyncApplyTransformPayload(data ?? [], (value) =>
            value.id === id ? { ...value, [key]: updatedValue } : value
          )
        );
      },
      updateTimeOff: function (timeOff: SchedulingTimeOffWithoutCursor) {
        rest.dispatch(
          getAsyncApplyTransformPayload(data ?? [], (value) => (value.id === timeOff.id ? timeOff : value))
        );
      },
      ...rest,
    }),
    [data, memoizedData, rest]
  );
}
